import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { FormProvider, TextBox } from "@/components/form";
import type { SystemSettingsData } from "./useSystemSettings";

const schema = yup.object().shape({
  max_failed_login_attempts: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(1, "Minimum 1 attempt")
    .max(20, "Maximum 20 attempts"),
  lockout_duration_minutes: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(1, "Minimum 1 minute")
    .max(1440, "Maximum 1440 minutes (24 hours)"),
  password_min_length: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(6, "Minimum 6 characters")
    .max(32, "Maximum 32 characters"),
  password_require_uppercase: yup.boolean().required(),
  password_require_lowercase: yup.boolean().required(),
  password_require_number: yup.boolean().required(),
  password_require_special: yup.boolean().required(),
  password_history_count: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(0, "Minimum 0 (disabled)")
    .max(24, "Maximum 24 remembered passwords"),
  mfa_enforced: yup.boolean().required(),
  mfa_remember_days: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(0, "Minimum 0 (disabled)")
    .max(90, "Maximum 90 days"),
  mfa_app_name: yup.string().default(""),
  email_sender_email: yup.string().email("Must be a valid email").default(""),
  email_sender_name: yup.string().default(""),
  smtp_host: yup.string().default(""),
  smtp_port: yup
    .number()
    .typeError("Must be a number")
    .default(587)
    .min(1, "Invalid port")
    .max(65535, "Invalid port"),
  smtp_user: yup.string().default(""),
  smtp_password: yup.string().default(""),
  password_reset_token_expiry: yup
    .number()
    .typeError("Must be a number")
    .required("Required")
    .min(300, "Minimum 5 minutes (300 seconds)")
    .max(86400, "Maximum 24 hours (86400 seconds)"),
});

type FormValues = yup.InferType<typeof schema>;

interface SecurityFormProps {
  settings: SystemSettingsData;
  onSave: (data: FormValues) => void;
  isUpdating: boolean;
  activeTab: number;
}

type PasswordSubTab = "policy" | "history" | "reset";

// Fields that belong to each tab — used to check per-tab validity
const TAB_FIELDS: Record<number, (keyof FormValues)[]> = {
  0: ["mfa_enforced", "mfa_remember_days", "mfa_app_name"],
  1: ["max_failed_login_attempts", "lockout_duration_minutes"],
  2: [
    "password_min_length",
    "password_require_uppercase",
    "password_require_lowercase",
    "password_require_number",
    "password_require_special",
    "password_history_count",
    "password_reset_token_expiry",
  ],
  3: [
    "email_sender_email",
    "email_sender_name",
    "smtp_host",
    "smtp_port",
    "smtp_user",
    "smtp_password",
  ],
};

const SecurityForm: React.FC<SecurityFormProps> = ({ settings, onSave, isUpdating, activeTab }) => {
  const [passwordTab, setPasswordTab] = useState<PasswordSubTab>("policy");
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: toFormValues(settings),
  });

  useEffect(() => {
    methods.reset(toFormValues(settings));
  }, [settings, methods]);

  // Only check errors for the current tab's fields
  const tabErrors = methods.formState.errors;
  const currentTabFields = TAB_FIELDS[activeTab] ?? [];
  const hasTabErrors = currentTabFields.some((field) => field in tabErrors);

  const handleTabSave = async () => {
    // Validate only the current tab's fields
    const valid = await methods.trigger(currentTabFields);
    if (!valid) return;

    // Pick only the current tab's fields
    const allValues = methods.getValues();
    const tabData: Record<string, unknown> = {};
    for (const field of currentTabFields) {
      const val = allValues[field];
      // Coerce number fields: schema.describe().fields tells us the type,
      // but simpler to check if the schema field expects a number
      const fieldSchema = schema.fields[field];
      if (fieldSchema && "type" in fieldSchema && fieldSchema.type === "number" && typeof val === "string") {
        tabData[field] = val === "" ? undefined : Number(val);
      } else {
        tabData[field] = val;
      }
    }
    onSave(tabData as FormValues);
  };

  return (
    <FormProvider methods={methods} onSubmit={onSave}>
      <Stack spacing={2.5}>
        {activeTab === 0 && (
          <>
            <Typography variant="body2" color="text.secondary">
              When enforced, all system admins must set up MFA on their next
              sign-in. Admins can also opt-in voluntarily from their Security
              page.
            </Typography>
            <Controller
              name="mfa_enforced"
              control={methods.control}
              render={({ field }) => (
                <FormControlLabel
                  labelPlacement="start"
                  sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={isUpdating}
                      size="small"
                    />
                  }
                  label="Enforce MFA for all system admins"
                />
              )}
            />

            <TextBox
              name="mfa_remember_days"
              label="Remember device (days)"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 0, max: 90 } }}
              helperText="How many days a device stays trusted after MFA verification (0 to disable)"
            />

            <TextBox
              name="mfa_app_name"
              label="Authenticator app name"
              fullWidth
              disabled={isUpdating}
              helperText="Issuer name shown in the user's authenticator app (e.g. Verve Vero Platform)"
            />
          </>
        )}

        {activeTab === 1 && (
          <>
            <Typography variant="body2" color="text.secondary">
              Configure brute force protection for system admin accounts. When a
              user exceeds the maximum failed login attempts, their account will
              be locked for the specified duration.
            </Typography>
            <TextBox
              name="max_failed_login_attempts"
              label="Max failed login attempts"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 1, max: 20 } }}
            />
            <TextBox
              name="lockout_duration_minutes"
              label="Lockout duration (minutes)"
              type="number"
              fullWidth
              disabled={isUpdating}
              slotProps={{ htmlInput: { min: 1, max: 1440 } }}
            />
          </>
        )}

        {activeTab === 2 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={passwordTab}
                onChange={(_, val) => setPasswordTab(val as PasswordSubTab)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="policy" label="Policy" />
                <Tab value="history" label="History" />
                <Tab value="reset" label="Reset" />
              </Tabs>
            </Box>

            {passwordTab === "policy" && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Configure password complexity requirements for system admin
                  accounts.
                </Typography>
                <TextBox
                  name="password_min_length"
                  label="Minimum password length"
                  type="number"
                  fullWidth
                  disabled={isUpdating}
                  slotProps={{ htmlInput: { min: 6, max: 32 } }}
                />
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Controller
                    name="password_require_uppercase"
                    control={methods.control}
                    render={({ field }) => (
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                        control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                        label="Require uppercase letter (A-Z)"
                      />
                    )}
                  />
                  <Controller
                    name="password_require_lowercase"
                    control={methods.control}
                    render={({ field }) => (
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                        control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                        label="Require lowercase letter (a-z)"
                      />
                    )}
                  />
                  <Controller
                    name="password_require_number"
                    control={methods.control}
                    render={({ field }) => (
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                        control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                        label="Require number (0-9)"
                      />
                    )}
                  />
                  <Controller
                    name="password_require_special"
                    control={methods.control}
                    render={({ field }) => (
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{ ml: 0, mr: 0, alignSelf: "flex-start", "& .MuiFormControlLabel-label": { mr: 1 } }}
                        control={<Checkbox checked={field.value} onChange={field.onChange} disabled={isUpdating} size="small" />}
                        label="Require special character (!@#$%^&*)"
                      />
                    )}
                  />
                </Stack>
              </>
            )}

            {passwordTab === "history" && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Prevent users from reusing recent passwords when changing their
                  password.
                </Typography>
                <TextBox
                  name="password_history_count"
                  label="Remembered passwords"
                  type="number"
                  fullWidth
                  disabled={isUpdating}
                  slotProps={{ htmlInput: { min: 0, max: 24 } }}
                />
              </>
            )}

            {passwordTab === "reset" && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Configure how long password reset links remain valid.
                </Typography>
                <TextBox
                  name="password_reset_token_expiry"
                  label="Reset link expiry (seconds)"
                  type="number"
                  fullWidth
                  disabled={isUpdating}
                  slotProps={{ htmlInput: { min: 300, max: 86400 } }}
                  helperText="How long the password reset link stays valid (default: 900 = 15 minutes)"
                />
              </>
            )}
          </>
        )}

        {activeTab === 3 && (
          <>
            <Typography variant="body2" color="text.secondary">
              Configure SMTP settings for sending password reset and
              notification emails. Credentials are stored securely.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 2,
                rowGap: 2,
              }}
            >
              <TextBox
                name="email_sender_email"
                label="Sender email address"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. noreply@yourdomain.com"
              />
              <TextBox
                name="email_sender_name"
                label="Sender display name"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. Verve Vero Platform"
              />
              <TextBox
                name="smtp_host"
                label="SMTP host"
                fullWidth
                disabled={isUpdating}
                helperText="e.g. smtp.gmail.com"
              />
              <TextBox
                name="smtp_port"
                label="SMTP port"
                type="number"
                fullWidth
                disabled={isUpdating}
                slotProps={{ htmlInput: { min: 1, max: 65535 } }}
              />
              <TextBox
                name="smtp_user"
                label="SMTP username"
                fullWidth
                disabled={isUpdating}
                helperText="Email or username for SMTP authentication"
              />
              <TextBox
                name="smtp_password"
                label="SMTP password"
                type="password"
                fullWidth
                disabled={isUpdating}
                helperText="Leave empty to keep existing password unchanged"
              />
            </Box>
          </>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleTabSave}
            disabled={isUpdating || hasTabErrors}
          >
            {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save Settings"}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

function toFormValues(s: SystemSettingsData): FormValues {
  return {
    max_failed_login_attempts: s.max_failed_login_attempts,
    lockout_duration_minutes: s.lockout_duration_minutes,
    password_min_length: s.password_min_length,
    password_require_uppercase: s.password_require_uppercase,
    password_require_lowercase: s.password_require_lowercase,
    password_require_number: s.password_require_number,
    password_require_special: s.password_require_special,
    password_history_count: s.password_history_count,
    mfa_enforced: s.mfa_enforced,
    mfa_remember_days: s.mfa_remember_days,
    mfa_app_name: s.mfa_app_name ?? "",
    email_sender_email: s.email_sender_email ?? "",
    email_sender_name: s.email_sender_name ?? "",
    smtp_host: s.smtp_host ?? "",
    smtp_port: s.smtp_port ?? 587,
    smtp_user: s.smtp_user ?? "",
    smtp_password: "",
    password_reset_token_expiry: s.password_reset_token_expiry,
  };
}

export default SecurityForm;
